import type { SessionRecorder } from '../sessionRecorder.js';
import { getDisplayName, isBroadcasting, setDisplayName } from '../streamIdentity.js';

const FELT = '#0d2c1d';
const GOLD = '#d4af37';

let pillEl: HTMLElement | null = null;
let recorderRef: SessionRecorder | null = null;
let promptNameFn: (() => string | null) | undefined;

export function mountBroadcastSlider(recorder: SessionRecorder, opts?: { promptName?: () => string | null }): void {
  if (pillEl) return;
  recorderRef = recorder;
  promptNameFn = opts?.promptName;

  // Build the pill container
  const pill = document.createElement('label');
  pill.setAttribute('data-broadcast-slider', '');
  pill.setAttribute('aria-label', 'Broadcast live');
  pill.setAttribute('title', 'Broadcast live');
  pill.style.cssText = [
    'position:fixed',
    'bottom:calc(env(safe-area-inset-bottom, 0px) + 12px)',
    'left:calc(env(safe-area-inset-left, 0px) + 12px)',
    'z-index:1000',
    'display:none', // hidden by default; setBroadcastSliderVisible toggles
    'align-items:center',
    'gap:6px',
    'padding:6px 12px 6px 8px',
    'border-radius:999px',
    `background:${FELT}`,
    `border:2px solid ${GOLD}`,
    'box-shadow:0 4px 14px rgba(0,0,0,0.5)',
    'cursor:pointer',
    'user-select:none',
    'font-family:"Helvetica Neue", Helvetica, Arial, sans-serif',
    'font-size:12px',
    'font-weight:700',
    'letter-spacing:0.06em',
    `color:${GOLD}`,
    'transition:box-shadow 120ms',
    'min-height:44px',
    'box-sizing:border-box',
  ].join(';');

  // Hidden checkbox powers the toggle
  const chk = document.createElement('input');
  chk.type = 'checkbox';
  chk.setAttribute('data-broadcast-toggle', '');
  chk.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:0;height:0;';
  chk.checked = isBroadcasting();

  // Visual slider track + thumb
  const track = document.createElement('span');
  track.setAttribute('aria-hidden', 'true');
  track.style.cssText = [
    'position:relative',
    'display:inline-block',
    'width:34px',
    'height:20px',
    'border-radius:10px',
    `background:${chk.checked ? GOLD : 'rgba(255,255,255,0.15)'}`,
    'transition:background 160ms',
    'flex-shrink:0',
  ].join(';');
  const thumb = document.createElement('span');
  thumb.style.cssText = [
    'position:absolute',
    'top:3px',
    `left:${chk.checked ? '17px' : '3px'}`,
    'width:14px',
    'height:14px',
    'border-radius:50%',
    'background:#fff',
    'transition:left 160ms',
  ].join(';');
  track.appendChild(thumb);

  // Label text: "LIVE" or "OFF"
  const lbl = document.createElement('span');
  lbl.textContent = chk.checked ? 'LIVE' : 'OFF';

  pill.appendChild(chk);
  pill.appendChild(track);
  pill.appendChild(lbl);

  // Toggle handler
  chk.addEventListener('change', () => void onToggle(chk, track, thumb, lbl));

  // Tapping the pill body while ON re-shares the link
  pill.addEventListener('click', (e) => {
    // The label click will fire the checkbox change; we only want the
    // re-share shortcut when the toggle is already ON and the user taps
    // the pill body (not toggling off). We detect this by checking if the
    // click target is the pill itself (not the checkbox proxy).
    if (chk.checked && e.target !== chk) {
      // Will be caught after the checkbox change fires — if already ON
      // the change won't fire again. Share directly.
      // But we must NOT double-trigger when the toggle flips to ON.
    }
  });

  document.body.append(pill);
  pillEl = pill;
}

export function setBroadcastSliderVisible(visible: boolean): void {
  if (!pillEl) return;
  pillEl.style.display = visible ? 'flex' : 'none';
}

async function onToggle(
  chk: HTMLInputElement,
  track: HTMLSpanElement,
  thumb: HTMLSpanElement,
  lbl: HTMLSpanElement,
): Promise<void> {
  if (chk.checked) {
    // Turning ON
    // Ensure a display name exists
    if (!getDisplayName()) {
      const name = promptNameFn ? promptNameFn() : window.prompt('Name to show watchers?');
      if (!name || !name.trim()) {
        // User cancelled — revert toggle
        chk.checked = false;
        return;
      }
      setDisplayName(name.trim());
    }
    await recorderRef?.startStreaming();
    updateVisual(chk.checked, track, thumb, lbl);
    // Surface the watch link
    const url = recorderRef?.shareUrl();
    if (url) await shareLink(url);
  } else {
    // Turning OFF
    recorderRef?.stopStreaming();
    updateVisual(false, track, thumb, lbl);
  }
}

function updateVisual(on: boolean, track: HTMLSpanElement, thumb: HTMLSpanElement, lbl: HTMLSpanElement): void {
  track.style.background = on ? GOLD : 'rgba(255,255,255,0.15)';
  thumb.style.left = on ? '17px' : '3px';
  lbl.textContent = on ? 'LIVE' : 'OFF';
}

// ── Toast + native share / clipboard fallback (ported from the old share button) ────

let toastEl: HTMLDivElement | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

async function shareLink(url: string): Promise<void> {
  try {
    if (typeof navigator.share === 'function') {
      await navigator.share({ title: 'Watch me play craps', url });
      return;
    }
  } catch { /* user cancelled or share threw — fall through */ }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      showFallbackToast('Link copied!');
      return;
    }
  } catch { /* fall through */ }
  showFallbackToast(`Copy this link:\n${url}`);
}

function showFallbackToast(message: string): void {
  if (toastEl) toastEl.remove();
  if (toastTimer) clearTimeout(toastTimer);

  const t = document.createElement('div');
  t.textContent = message;
  t.style.cssText = [
    'position:fixed',
    'bottom:calc(env(safe-area-inset-bottom, 0px) + 70px)',
    'left:50%',
    'transform:translateX(-50%)',
    'background:rgba(0,0,0,0.92)',
    `color:${GOLD}`,
    'font-family:"Helvetica Neue", Helvetica, Arial, sans-serif',
    'font-size:13px',
    'padding:10px 14px',
    'border-radius:8px',
    `border:1px solid ${GOLD}`,
    'box-shadow:0 4px 14px rgba(0,0,0,0.5)',
    'z-index:1001',
    'max-width:90vw',
    'word-break:break-all',
    'white-space:pre-line',
    'text-align:center',
    'cursor:pointer',
    'user-select:text',
  ].join(';');
  t.addEventListener('click', () => { t.remove(); toastEl = null; });
  document.body.append(t);
  toastEl = t;
  toastTimer = setTimeout(() => {
    if (toastEl === t) { t.remove(); toastEl = null; }
  }, 6000);
}
